import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { URL_HELPER } from "../App";
import { Modal } from "@coreui/coreui/dist/js/coreui";
import iconClose from "../assets/icons/close.svg";
import type { CartItem } from "./utils/CartHelper";
import { RenditionItem, downloadCheckoutItems } from "./utils/CheckoutHelper";
import type { CheckoutItems, CheckoutModalProps } from "./utils/CheckoutHelper";
import downloadIcon from "../assets/icons/download.svg";

export const CheckoutModal: React.FC<CheckoutModalProps> = ({
  cart,
  setCart,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const popupEl = useRef<HTMLDivElement>(null);

  const [checkoutItems, setCheckoutItems] = useState<CheckoutItems[]>([]);

  function getCheckoutItems(): CheckoutItems[] {
    const items: CheckoutItems[] = [];
    cart.forEach((asset) => {
      getRenditions(asset).then((renditions) => {
        const newRenditions = renditions.map((rendition) => {
          return new RenditionItem(asset.id, rendition);
        });
        const defaultRendition = newRenditions.find(
          (rendition) => rendition.name === "Medium"
        );
        const item: CheckoutItems = {
          id: asset.id,
          name: asset.name,
          extension: defaultRendition?.extension || asset.extension,
          renditions: newRenditions,
          downloadUrl: defaultRendition?.url || newRenditions[0].url,
        };
        items.push(item);
      });
    });
    return items;
  }

  function getRenditions(
    asset: CartItem
  ): Promise<{ name: string; contentLength: number; extension: string }[]> {
    const renditions = fetch(URL_HELPER.getRenditionsUrl() + "/" + asset.id, {
      method: "GET",
    })
      .then((response) => response.json())
      .then((data) => {
        return data;
      })
      .catch((error) => {
        console.error("Error:", error);
      });
    return renditions;
  }

  function handleRenditionChange(
    asset: CheckoutItems,
    event: React.ChangeEvent<HTMLSelectElement>
  ) {
    const url = event.target.value;
    if (url !== asset.downloadUrl) {
      asset.downloadUrl = url;
      const newExtension = asset.renditions.find(
        (rendition) => rendition.url === url
      )?.extension;
      if (newExtension) {
        asset.extension = newExtension;
      }
    }
  }

  function getCombinedFileSize(): number {
    let size = 0;
    checkoutItems.forEach((item) => {
      item.renditions.forEach((rendition) => {
        if (item.downloadUrl === rendition.url) {
          size += rendition.contentLength;
        }
      });
    });
    return size;
  }

  function startDownload() {
    downloadCheckoutItems(checkoutItems);
    setCart([]);
    closePopup();
  }

  useEffect(() => {
    setCheckoutItems(getCheckoutItems());
  }, [cart, setCart, setCheckoutItems]);

  function openPopup() {
    setOpen(true);
  }

  function closePopup() {
    setOpen(false);
  }

  function bytesToSize(bytes: number): string {
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "0 Byte";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    if (i < 2) {
      const size = Math.round(bytes / Math.pow(1024, i));
      return size + " " + sizes[i];
    } else {
      const size = (bytes / Math.pow(1024, i)).toFixed(1);
      return size + " " + sizes[i];
    }
  }

  function renderPopup() {
    return (
      <div className="modal-dialog checkout-modal" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h3 title="Checkout">Checkout</h3>
            </div>
            <img
              className="icon"
              src={iconClose}
              aria-label="Close"
              data-dismiss="modal"
              role="button"
              onClick={closePopup}
              alt="Clear"
              title="Clear"
            />
          </div>
          <div className="modal-body">
            <ul>
              {checkoutItems.map((asset, index) => (
                <li key={index}>
                  <div>{asset.name}</div>
                  {asset.renditions.length ? (
                    <select
                      className="form-control"
                      name="renditions"
                      id={"renditions-" + index}
                      onChange={(event) => handleRenditionChange(asset, event)}
                      defaultValue={asset.downloadUrl}
                    >
                      {" "}
                      {asset.renditions.map((rendition) => (
                        <option value={rendition.url} key={rendition.name}>
                          {rendition.name} (
                          {bytesToSize(rendition.contentLength)})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="form-control">ORIGINAL</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary btn-download"
              data-dismiss="modal"
              onClick={startDownload}
            >
              <img
                src={downloadIcon}
                className="icon icon-md icon-white"
                alt=""
              />
              Download ({bytesToSize(getCombinedFileSize())})
            </button>
          </div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    document.addEventListener("open-checkout", openPopup);
    return () => {
      window.removeEventListener("open-checkout", openPopup);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (popupEl.current) {
      let modal = new Modal(popupEl.current);
      if (open) {
        modal.show();
      } else {
        modal.hide();
      }
    }
  }, [open]);

  return (
    <div
      className="asset-modal modal fade"
      role="dialog"
      ref={popupEl}
      onClick={closePopup}
    >
      {renderPopup()}
    </div>
  );
};

export default CheckoutModal;
